import { useState } from "react";
import PropTypes from "prop-types";
import { cn } from "@/lib/utils";
import { initials, userLabel } from "../tasks/userDisplay";

const SIZES = {
  xs: "size-5 text-[10px]",
  sm: "size-6 text-[11px]",
  md: "size-8 text-xs",
  lg: "size-16 text-xl",
};

/**
 * A person or agent as a small circle: their profile picture when they
 * have one, else the two letters the board has always shown
 * (photos-and-avatars), drawn by CSS so they never enter the page text. Next to a name, pass `decorative` — the name is
 * already read out; on its own, the circle is labelled ("scout (agent)").
 * A picture that fails to load (an expired link, a removed picture) falls
 * back to the letters rather than a broken image.
 */
function UserAvatar({ user, size = "sm", decorative = false, className }) {
  const [failedSrc, setFailedSrc] = useState(null);
  const src = user?.avatar_url || null;
  const label = userLabel(user) || "someone";
  const a11y = decorative
    ? { "aria-hidden": true }
    : { role: "img", "aria-label": label, title: label };
  const base = cn(
    "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary font-semibold text-secondary-foreground uppercase select-none dark:bg-background",
    SIZES[size],
    className,
  );

  if (src && failedSrc !== src) {
    return (
      <span className={base} {...a11y}>
        <img
          src={src}
          alt=""
          referrerPolicy="no-referrer"
          loading="lazy"
          className="size-full object-cover"
          onError={() => setFailedSrc(src)}
        />
      </span>
    );
  }
  // The letters are drawn by CSS, not written into the page: beside a name
  // they'd otherwise run into it in the text people copy and agents read
  // ("Created by brbrandon").
  return (
    <span
      className={cn(base, "before:content-[attr(data-initials)]")}
      data-initials={initials(user)}
      {...a11y}
    />
  );
}

UserAvatar.propTypes = {
  user: PropTypes.shape({
    username: PropTypes.string,
    kind: PropTypes.string,
    avatar_url: PropTypes.string,
  }),
  size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
  decorative: PropTypes.bool,
  className: PropTypes.string,
};

export default UserAvatar;
